import { Brand, checkExhaustive, create, ifNull } from '@/ts_utils'
import { bannedDependencies, bannedPrereqs } from '@/utils/graph'
import * as solver from '@/utils/solver'
import * as v2 from '@/utils/vector2'
import { Manager } from 'hammerjs'
import normalizeWheel from 'normalize-wheel'
import { fromEvent, NEVER, Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'
import { Component, Emit, Prop, Provide, Ref, Vue } from 'vue-property-decorator'
import { HALF_TASK_HEIGHT, HALF_TASK_WIDTH } from '../config'
import { Dependency, DependencyId, Store, Task, TaskId } from '../model/types'
import { clamp } from '../utils/math'
import DependencyEdge from './DependencyEdge.vue'
import Minimap from './Minimap.vue'
import TaskNode from './TaskNode.vue'

const MIN_ZOOM = 0.2
const MAX_ZOOM = 3.0

// The states of various draft objects and the behavior of the UI to certain
// events are grouped in a single place so that they can be returned by a single
// getter because the logic for computing them is highly interdependent. (e.g.
// It depends on the current editor mode and the last mouse position.)
interface ModeBehavior {
  draftDependency: {
    prereq: Task;
    dependent: Task;
  } | null;
  draftTask: Task | null;

  handleTap(at: ScreenCoordinates, raw: HammerInput): void;
  handleEscape(raw: KeyboardEvent): void;
}

const POINTER_MODE_BEHAVIOR: ModeBehavior = Object.freeze({
  draftDependency: null,
  draftTask: null,

  handleTap () {
    // No-op
  },
  handleEscape () {
    // No-op
  }
})

interface PointerMode {
  id: 'pointer';
}

interface AddDependentMode {
  id: 'addDependent';
  from: TaskId;
}

interface AddPrereqMode {
  id: 'addPrerequisite';
  to: TaskId;
}

type EditorMode = PointerMode | AddDependentMode | AddPrereqMode

const POINTER_MODE: PointerMode = Object.freeze({ id: 'pointer' })

// We have many coordinate systems at play here:
//   1. Coordinates within the screen
//   2. Coordinates within the graph element
//   3. Logical coordinates in the graph
//
// The editor instance converts between coordinate systems since it has all of
// the scaling factors and offsets and controls the layout of the UI. Here's the
// conversion sequence:
//
//   Screen -> Element <-> Graph
export type ScreenCoordinates = Brand<v2.Vector2, 'screen'>
export type ElementCoordinates = Brand<v2.Vector2, 'element'>
export type GraphCoordinates = Brand<v2.Vector2, 'graph'>

function getTask (e: Element): TaskId | null {
  let target: Element | null = e
  while (target) {
    // Extra attribute set on TaskNode elements
    const marker = target.attributes.getNamedItem('taskMarker')
    if (marker) return marker.value
    target = target.parentElement
  }
  return null
}

function getTaskAt (at: ScreenCoordinates): TaskId | null {
  let targetId: TaskId | null = null
  const elements = document.elementsFromPoint(at.x, at.y)
  for (const e of elements) {
    targetId = getTask(e)
    if (targetId) return targetId
  }
  return null
}

function makeTask (props: Pick<Task, 'x' | 'y'> & Partial<Task>): Task {
  return {
    title: 'New Task',
    createdAt: new Date(),
    deletedAt: null,
    completedAt: null,
    duration: null,
    estimatedCompletion: null,
    priority: null,
    ...props
  }
}

@Component({
  components: {
    DependencyEdge,
    Minimap,
    TaskNode
  }
})
export default class ProjectEditor extends Vue {
  @Prop() private store!: Store;

  @Ref('graph') readonly graph!: HTMLElement;

  @Provide() get editor () {
    return this
  }

  private hammer!: HammerManager;
  private subscription: Subscription = NEVER.subscribe()

  private mode: EditorMode = POINTER_MODE
  private lastMouse: ScreenCoordinates = v2.ZERO as ScreenCoordinates

  mounted () {
    const hammer = new Manager(this.graph)
    hammer.add(new Hammer.Tap())
    const pan2 = hammer.add(new Hammer.Pan({ pointers: 0 }))
    hammer.add(new Hammer.Pinch()).recognizeWith(pan2)

    this.hammer = hammer

    hammer.on('tap', (e) => {
      this.modeBehavior.handleTap(e.center as ScreenCoordinates, e)
    })

    let lastDeltaX = 0
    let lastDeltaY = 0
    let startZoom = 1
    let moveFunction: (x: number, y: number) => void
    hammer.on('hammer.input', (e) => {
      if (!e.isFirst) return

      lastDeltaX = 0
      lastDeltaY = 0
      startZoom = this.store.camera.zoom

      const targettedTask = getTask(e.target)
      if (targettedTask) {
        moveFunction = (x: number, y: number) => {
          const task = this.store.project.tasks[targettedTask]
          if (!task) return
          task.x += x
          task.y += y
        }
      } else {
        moveFunction = (x: number, y: number) => {
          this.store.camera.xOff += x
          this.store.camera.yOff -= y
          this.clampCamera()
        }
      }
    })
    hammer.on('panmove', (e) => {
      const zoom = this.store.camera.zoom
      const stepX = (e.deltaX - lastDeltaX) / zoom
      const stepY = (lastDeltaY - e.deltaY) / zoom
      moveFunction(stepX, stepY)
      lastDeltaX = e.deltaX
      lastDeltaY = e.deltaY
    })
    hammer.on('pinchmove', (e) => {
      const clientX = e.center.x
      const clientY = e.center.y
      const startMouseX = this.elementToGraphX(clientX)
      const startMouseY = this.elementToGraphY(clientY)
      this.store.camera.zoom = startZoom * e.scale
      this.store.camera.xOff += this.elementToGraphX(clientX) - startMouseX
      this.store.camera.yOff += this.elementToGraphY(clientY) - startMouseY
      this.clampCamera()
    })

    this.subscription.add(
      fromEvent(window, 'resize')
        .subscribe(() => this.handleResize())
    )
    this.handleResize()

    this.subscription.add(
      fromEvent<KeyboardEvent>(document, 'keyup')
        .pipe(filter(e => e.key === 'Escape'))
        .subscribe(e => this.modeBehavior.handleEscape(e))
    )
  }

  beforeDestroy () {
    this.hammer.destroy()
    this.subscription.unsubscribe()
  }

  private handleResize () {
    this.store.camera.width = this.graph.clientWidth
    this.store.camera.height = this.graph.clientHeight
  }

  private handleWheel (e: WheelEvent) {
    // TODO: Zoom in/out around scroll location
    const normalized = normalizeWheel(e)
    const clientX = e.clientX
    const clientY = e.clientY
    const startMouseX = this.elementToGraphX(clientX)
    const startMouseY = this.elementToGraphY(clientY)
    this.store.camera.zoom = this.store.camera.zoom + this.store.camera.zoom * -normalized.pixelY * 0.001
    this.store.camera.xOff += this.elementToGraphX(clientX) - startMouseX
    this.store.camera.yOff -= this.elementToGraphY(clientY) - startMouseY
    this.clampCamera()
  }

  private handleNewTask () {
    const visualCenter = this.elementToGraph({
      x: this.store.camera.width / 2,
      y: this.store.camera.height / 2
    } as ElementCoordinates)

    this.addTask(visualCenter)
  }

  @Emit()
  private expandSidebar () {
    // No-op, since decorator emits event.
  }

  private addTask (props: Pick<Task, 'x' | 'y'> & Partial<Task>): TaskId {
    let id: TaskId
    do {
      id = Math.round(Math.random() * 999999).toString()
    } while (id in this.store.project.tasks)

    Vue.set(this.store.project.tasks, id, makeTask(props))

    this.changeEstimate()

    return id
  }

  private addDependency (props: Pick<Dependency, 'prereq' | 'dependent'> & Partial<Dependency>): TaskId {
    let id: DependencyId
    do {
      id = Math.round(Math.random() * 999999).toString()
    } while (id in this.store.project.dependencies)

    Vue.set(this.store.project.dependencies, id, create<Dependency>({
      createdAt: new Date(),
      deletedAt: null,
      ...props
    }))

    this.changeEstimate()

    return id
  }

  private handleAddPrerequsite (id: TaskId) {
    this.mode = { id: 'addPrerequisite', to: id }
  }

  private handleAddDependent (id: TaskId) {
    this.mode = { id: 'addDependent', from: id }
  }

  private handleDeleteTask (id: DependencyId) {
    Vue.delete(this.store.project.tasks, id)
    for (const [depId, dep] of Object.entries(this.store.project.dependencies)) {
      if (dep.prereq === id || dep.dependent === id) {
        Vue.delete(this.store.project.dependencies, depId)
      }
    }

    this.changeEstimate()
  }

  private handleDeleteDependency (id: DependencyId) {
    Vue.delete(this.store.project.dependencies, id)

    this.changeEstimate()
  }

  private handleMouseMove (e: MouseEvent) {
    this.lastMouse = {
      x: e.clientX,
      y: e.clientY
    } as ScreenCoordinates
  }

  private changeEstimate () {
    // Before we run the time solver, we want to deduplicate and condense task
    // priorities. Any tasks with null priorities should remain null.
    //
    // Example: If we have the following priorities A: 1, B,: 2, C: 4, and we
    //   just changed D to have priority 2, then the priorities should come out
    //   to: A: 1, D: 2, B: 3, C: 4.
    const placeholder = Number.MAX_SAFE_INTEGER
    const tasks = Object.values(this.store.project.tasks)
    tasks.sort((a, b) => {
      const aPriority = ifNull(a.priority, placeholder)
      const bPriority = ifNull(b.priority, placeholder)
      if (aPriority < bPriority) return -1
      else if (aPriority > bPriority) return 1
      else return 0
    })

    let i = 1
    for (const task of tasks) {
      if (task.priority === null) break

      task.priority = i
      i++
    }

    // Finally, run the time solver.
    solver.solve(this.store.project)

    this.$forceUpdate()
  }

  graphToElement (v: GraphCoordinates): ElementCoordinates {
    return {
      x: this.graphToElementX(v.x),
      y: this.graphToElementY(v.y)
    } as ElementCoordinates
  }

  graphToElementX (x: number): number {
    return this.store.camera.width / 2 + (this.store.camera.xOff + x) * this.store.camera.zoom
  }

  graphToElementY (y: number): number {
    return this.store.camera.height / 2 + (this.store.camera.yOff - y) * this.store.camera.zoom
  }

  elementToGraph (v: ElementCoordinates): GraphCoordinates {
    return {
      x: this.elementToGraphX(v.x),
      y: this.elementToGraphY(v.y)
    } as GraphCoordinates
  }

  elementToGraphX (x: number): number {
    return (x - this.store.camera.width / 2) / this.store.camera.zoom - this.store.camera.xOff
  }

  elementToGraphY (y: number): number {
    return (this.store.camera.height / 2 - y) / this.store.camera.zoom + this.store.camera.yOff
  }

  screenToElement (screen: ScreenCoordinates): ElementCoordinates {
    const bounds = this.graph.getBoundingClientRect()
    return {
      x: screen.x - bounds.left,
      y: screen.y - bounds.top
    } as ElementCoordinates
  }

  private taskOffsetLeft (x: number): number {
    return this.graphToElementX(x) - HALF_TASK_WIDTH
  }

  private taskOffsetTop (y: number): number {
    return this.graphToElementY(y) - HALF_TASK_HEIGHT
  }

  // Given the current editor mode, compute a behavior object that customizes
  // the display of draft objects and handles certain types of events.
  private get modeBehavior (): Readonly<ModeBehavior> {
    if (this.mode.id === 'pointer') {
      return POINTER_MODE_BEHAVIOR
    } else if (this.mode.id === 'addDependent') {
      return this.makeDependentBehavior(this.mode)
    } else if (this.mode.id === 'addPrerequisite') {
      return this.makePrereqBehavior(this.mode)
    } else {
      checkExhaustive(this.mode)
    }
  }

  // Implementing as getter (computed property) to take advantage of Vue's
  // built-in caching.
  private get disallowedNodes (): Set<TaskId> {
    if (this.mode.id === 'pointer') {
      return new Set()
    } else if (this.mode.id === 'addDependent') {
      return bannedDependencies(this.store.project, this.mode.from)
    } else if (this.mode.id === 'addPrerequisite') {
      return bannedPrereqs(this.store.project, this.mode.to)
    } else {
      checkExhaustive(this.mode)
    }
  }

  private makePrereqBehavior (mode: AddPrereqMode): ModeBehavior {
    const toTask = this.store.project.tasks[mode.to]
    const fromTaskId = getTaskAt(this.lastMouse)
    let draftTask, draftDependency, create: () => void
    if (fromTaskId === null || this.disallowedNodes.has(fromTaskId)) {
      const graphPoint = this.elementToGraph(this.screenToElement(this.lastMouse))
      draftTask = makeTask({
        x: graphPoint.x,
        y: graphPoint.y
      })
      draftDependency = {
        prereq: draftTask,
        dependent: toTask
      }
      create = () => {
        const createdId = this.addTask({
          x: graphPoint.x,
          y: graphPoint.y
        })
        this.addDependency({
          prereq: createdId,
          dependent: mode.to
        })
      }
    } else {
      const fromTask = this.store.project.tasks[fromTaskId]
      draftTask = null
      draftDependency = {
        prereq: fromTask,
        dependent: toTask
      }
      create = () => {
        this.addDependency({
          prereq: fromTaskId,
          dependent: mode.to
        })
      }
    }

    return {
      draftDependency,
      draftTask,

      handleEscape: () => {
        this.mode = POINTER_MODE
      },
      handleTap: () => {
        this.mode = POINTER_MODE
        create()
      }
    }
  }

  private makeDependentBehavior (mode: AddDependentMode): ModeBehavior {
    const fromTask = this.store.project.tasks[mode.from]
    const toTaskId = getTaskAt(this.lastMouse)
    let draftTask, draftDependency, create: () => void
    if (toTaskId === null || this.disallowedNodes.has(toTaskId)) {
      const graphPoint = this.elementToGraph(this.screenToElement(this.lastMouse))
      draftTask = makeTask({
        x: graphPoint.x,
        y: graphPoint.y
      })
      draftDependency = {
        prereq: fromTask,
        dependent: draftTask
      }
      create = () => {
        const createdId = this.addTask({
          x: graphPoint.x,
          y: graphPoint.y
        })
        this.addDependency({
          prereq: mode.from,
          dependent: createdId
        })
      }
    } else {
      const toTask = this.store.project.tasks[toTaskId]
      draftTask = null
      draftDependency = {
        prereq: fromTask,
        dependent: toTask
      }
      create = () => {
        this.addDependency({
          prereq: mode.from,
          dependent: toTaskId
        })
      }
    }

    return {
      draftDependency,
      draftTask,

      handleEscape: () => {
        this.mode = POINTER_MODE
      },
      handleTap: () => {
        this.mode = POINTER_MODE
        create()
      }
    }
  }

  // Similar exists in Minimap.
  private get maxOffsets () {
    const xValues = Object.values(this.store.project.tasks).map(t => t.x)
    const yValues = Object.values(this.store.project.tasks).map(t => t.y)

    const max = (v: number[]) => v.reduce((a, b) => Math.max(a, b)) || 0
    const min = (v: number[]) => v.reduce((a, b) => Math.min(a, b)) || 0

    const scaledThirdWidth = (this.store.camera.width / 3) / this.store.camera.zoom
    const scaledThirdHeight = (this.store.camera.height / 3) / this.store.camera.zoom
    return {
      minX: -(max(xValues) + scaledThirdWidth),
      maxX: -(min(xValues) - scaledThirdWidth),
      minY: min(yValues) - scaledThirdHeight,
      maxY: max(yValues) + scaledThirdHeight
    }
  }

  private clampCamera () {
    const bounds = this.maxOffsets
    this.store.camera.xOff = clamp(this.store.camera.xOff, bounds.minX, bounds.maxX)
    this.store.camera.yOff = clamp(this.store.camera.yOff, bounds.minY, bounds.maxY)
    this.store.camera.zoom = clamp(this.store.camera.zoom, MIN_ZOOM, MAX_ZOOM)
  }
}
